import { tr } from '@/lib/i18n-core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FOLLOW_UP_VALID_DAYS_MAX,
  FOLLOW_UP_VALID_DAYS_MIN,
  PAID_NO_SHOW_POLICY,
  QUEUE_ORDER,
  SCREENING_BEFORE_READY,
  resolveOpdSettings,
  type OpdSettings,
} from '@his/shared';
import { get, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Field,
  Input,
} from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';

const SCREENING_LABEL: Record<string, string> = {
  off: 'Not asked — vitals make the patient ready',
  optional: 'Offered to the nurse',
  required: 'Required before the patient is ready',
};

const QUEUE_ORDER_LABEL: Record<string, string> = {
  token: 'Token order',
  slot_then_arrival: 'Appointment time, then arrival',
};

function NumberSetting({
  label,
  hint,
  value,
  min,
  max,
  disabled,
  onSave,
}: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  disabled: boolean;
  onSave: (value: number) => void;
}) {
  return (
    <Field label={label} hint={hint}>
      <Input
        type="number"
        min={min}
        max={max}
        disabled={disabled}
        defaultValue={value}
        key={`${label}-${value}`}
        onBlur={(e) => {
          const next = Number(e.target.value);
          if (Number.isInteger(next) && next >= min && next <= max && next !== value) onSave(next);
        }}
      />
    </Field>
  );
}

export function OpdClinicSettingsCard({ readOnly }: { readOnly: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => get<{ settings: Record<string, unknown> }>('/org/settings'),
  });
  const current = resolveOpdSettings(settings.data?.settings);

  const save = useMutation({
    mutationFn: (patch: Partial<OpdSettings>) => put('/org/settings', patch),
    onSuccess: () => {
      toast.success(tr('opd', 'clinic_setting_saved'));
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: (error) => toast.error(tr('opd', 'could_not_save_the_setting'), describeError(error)),
  });

  return (
    <>
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-sm">{tr('opd', 'outpatient_clinic_day')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm sm:grid-cols-2">
          <NumberSetting
            label={tr('opd', 'the_clinic_day_closes_at_hour_0_8')}
            hint={tr('opd', 'evening_sessions_that_run_past_midnight_belong')}
            value={current.dayClosesAt}
            min={0}
            max={8}
            disabled={readOnly}
            onSave={(dayClosesAt) => save.mutate({ dayClosesAt })}
          />
          <NumberSetting
            label={tr('opd', 'late_entry_allowed_for_days')}
            hint={tr('opd', 'visits_of_earlier_days_can_be_entered_by_staff')}
            value={current.backEntryDays}
            min={0}
            max={30}
            disabled={readOnly}
            onSave={(backEntryDays) => save.mutate({ backEntryDays })}
          />
          <Field label={tr('opd', 'patient_screening_at_the_nurse_station')} className="sm:col-span-2">
            <ChoiceButtons
              layout="cards"
              columns={3}
              value={current.screeningBeforeReady}
              disabled={readOnly}
              onChange={(screeningBeforeReady) => save.mutate({ screeningBeforeReady })}
              options={SCREENING_BEFORE_READY.map((value) => ({ value, label: SCREENING_LABEL[value] }))}
            />
          </Field>
          <Field label={tr('opd', 'who_is_called_next')}>
            <ChoiceButtons
              value={current.queueOrder}
              disabled={readOnly}
              onChange={(queueOrder) => save.mutate({ queueOrder })}
              options={QUEUE_ORDER.map((value) => ({ value, label: QUEUE_ORDER_LABEL[value] }))}
            />
          </Field>
          <NumberSetting
            label={tr('opd', 'online_bookings_per_patient')}
            hint={tr('opd', 'upcoming_appointments_a_patient_may_hold')}
            value={current.portalMaxUpcomingPerPatient}
            min={1}
            max={20}
            disabled={readOnly}
            onSave={(portalMaxUpcomingPerPatient) => save.mutate({ portalMaxUpcomingPerPatient })}
          />
          <div className="space-y-2 sm:col-span-2">
            <Checkbox
              checked={current.autoNoShow}
              disabled={readOnly}
              onCheckedChange={(autoNoShow) =>
                save.mutate(
                  autoNoShow
                    ? { autoNoShow, autoNoShowEnabledAt: new Date().toISOString() }
                    : { autoNoShow },
                )
              }
              label={tr('opd', 'close_each_clinic_day_mark_patients_who_never')}
            />
            <Checkbox
              checked={current.walkInWithoutSession}
              disabled={readOnly}
              onCheckedChange={(walkInWithoutSession) => save.mutate({ walkInWithoutSession })}
              label={tr('opd', 'take_walk_ins_for_a_doctor_who_has_no_clinic')}
            />
            <Checkbox
              checked={current.requirePrimaryDiagnosis}
              disabled={readOnly}
              onCheckedChange={(requirePrimaryDiagnosis) =>
                save.mutate({ requirePrimaryDiagnosis })
              }
              label={tr('opd', 'a_consultation_needs_a_principal_diagnosis')}
            />
          </div>
        </CardContent>
      </Card>
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-sm">{tr('opd', 'outpatient_billing')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <Alert>{tr('opd', 'patients_pay_the_visit_bill_on_arrival')}</Alert>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={tr('billing', 'paid_visit_not_attended')}>
              <ChoiceButtons
                value={current.paidNoShowPolicy}
                disabled={readOnly}
                onChange={(paidNoShowPolicy) => save.mutate({ paidNoShowPolicy })}
                options={PAID_NO_SHOW_POLICY.map((value) => ({
                  value,
                  label: value === 'keep' ? tr('opd', 'no_show_keep_the_fee') : tr('opd', 'no_show_flag_for_refund'),
                }))}
              />
            </Field>
            <NumberSetting
              label={tr('billing', 'keep_the_quoted_price_for_days')}
              hint={tr('billing', '0_bill_todays_price')}
              value={current.quoteValidDays}
              min={0}
              max={90}
              disabled={readOnly}
              onSave={(quoteValidDays) => save.mutate({ quoteValidDays })}
            />
            <NumberSetting
              label={tr('billing', 'follow_up_offer_valid_for_days')}
              hint={tr('billing', 'follow_up_offer_valid_for_days_hint')}
              value={current.followUpValidDays}
              min={FOLLOW_UP_VALID_DAYS_MIN}
              max={FOLLOW_UP_VALID_DAYS_MAX}
              disabled={readOnly}
              onSave={(followUpValidDays) => save.mutate({ followUpValidDays })}
            />
          </div>
          <Checkbox
            checked={current.tokenAfterPayment}
            disabled={readOnly}
            onCheckedChange={(tokenAfterPayment) => save.mutate({ tokenAfterPayment })}
            label={tr('billing', 'give_the_token_only_after_the_fee_is_paid')}
          />
        </CardContent>
      </Card>
    </>
  );
}
